import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { createLinqsetNode, CURRENT_STATE_PROJECT_KEY, getInsights, getInsightsForNode, getSuperTypeName, INSIGHTS_FIELD, INSIGHTS_SECTION, isModelNode, Linqset, LINQSET_TYPE, LinqsetNode, MODEL_TYPE, NODE_SUPERTYPE, NODE_TYPE, NodeInsights, sumAllocations } from "../common/linqset-model";
import { createLinqset } from "../common/model-processing";
import { ACTION_TYPE, CARBON_SCOPE, createInsightsFilter, getActionTypeName, getCarbonScopeDescription, getCostsForType, getPeopleRoleModelCost, getSystemInfrastructureModelCost, getTotalModelCost, getUnsortedModelList, InsightsFilter, MODEL_FUNCTION, ModelDetails, ModelInsights, Tag, totalModelInsights, DEFAULT_CURRENCY, getCurrency } from "../common/organisation";
import { InsightsRow } from "./components/top-five.component";
import { MatChipListboxChange } from "@angular/material/chips";
import { FormattingService } from "../linq-utils/formatting/formatting.service";
import { escapeHtml } from "../linq-utils/formatting/html";
import { colorAsARGB, getActionTypeColour, MODEL_COLOURS } from "../model-services/model-item-enums";
import { contributionField, InsightsFieldType } from "./model/insights-field-type";
import { NODE_TYPE_DATA_TYPE } from "./node-types/node-type-data";

import { MatDialog } from "@angular/material/dialog";
import { CommentDialogComponent, CommentDialogData } from "../ui-components/dialogs/comment-dialog.component";
import { UserService } from "../user/user.service";
import { Subscription } from "rxjs";
import { GraphProcessingService } from "../model-services/graph-processing.service";
import { OrganisationService } from "../organisation/organisation.service";
import { AppComponent } from "../app.component";
import { IGraph } from "@yfiles/yfiles";
import { loadJsPDF, PdfService } from "../model-services/pdf.service";
import { LinqChartData } from "../charts/linq-chart-data";
import { User } from "../common/user";
import { INSIGHT_VALUE_TYPE, InsightCard } from "../ui-components/insight-cards/insight-cards.component";
import { TagsService } from "../organisation/tags/tags-service";
import { CarouselSlide } from "../ui-components/carousel/popup-carousel.component";
import { Router } from "@angular/router";
import { OverviewData, OverviewService } from "../analysis/overview.service";
import { LEVER_LABELS, previewRecommendations, Recommendation } from "../analysis/recommendations";
import { AnalysisScope, ScopeService, scopeFromFilter } from "../analysis/scope";
import { LENSES, LensKey } from "../lenses/lens";
import { BubbleData, BubbleDataSet } from "../charts/bubble-chart/bubble-chart.component";

// value and revenue show as contributions in the sections where they apply (see contributionField), so they aren't chosen on their own
const ignoreTypes = [INSIGHTS_FIELD.NONE, INSIGHTS_FIELD.INFORMATION_SUPPLIED, INSIGHTS_FIELD.VALUE_CONTRIBUTION, INSIGHTS_FIELD.REVENUE_CONTRIBUTION]

const PDF_SCALE = 1.2               // capture resolution for the PDF
const PDF_MAX_CANVAS_HEIGHT = 30000 // browsers can't draw canvases much taller than 32,767 pixels
const PDF_SECTION_GAP = 3           // mm between sections in the PDF
const defaultTypes = [INSIGHTS_FIELD.ANNUAL_HOURS, INSIGHTS_FIELD.PROCESSED, INSIGHTS_FIELD.ACTIONS_SUPPORTED, INSIGHTS_FIELD.AUTOMATED_ACTIONS]
const defaultSections = [INSIGHTS_SECTION.FILTER, INSIGHTS_SECTION.OVERVIEW, INSIGHTS_SECTION.MODELS, INSIGHTS_SECTION.ACTIONS, INSIGHTS_SECTION.INPUTS, INSIGHTS_SECTION.OUTPUTS, INSIGHTS_SECTION.ROLES, INSIGHTS_SECTION.SYSTEMS]

const noPercentages = [INSIGHTS_FIELD.WORKLOAD, INSIGHTS_FIELD.PROCESSED, INSIGHTS_FIELD.CARBON_ITEM, INSIGHTS_FIELD.COST_ITEM, INSIGHTS_FIELD.HOURS_ITEM, INSIGHTS_FIELD.ACTIONS_SUPPORTED, INSIGHTS_FIELD.CARBON_COST, INSIGHTS_FIELD.INFORMATION_SUPPLIED, INSIGHTS_FIELD.OUTPUTS, INSIGHTS_FIELD.PROCESSED, INSIGHTS_FIELD.VALUE, INSIGHTS_FIELD.DERIVED_VALUE, INSIGHTS_FIELD.EXCEPTION_WORK, INSIGHTS_FIELD.REVENUE_MARGIN, INSIGHTS_FIELD.REVENUE_AT_RISK]

const organisationSections = [INSIGHTS_SECTION.WORKSPACES, INSIGHTS_SECTION.MODELS, INSIGHTS_SECTION.MODEL_FUNCTIONS, INSIGHTS_SECTION.MODEL_TAGS, INSIGHTS_SECTION.PEOPLE, INSIGHTS_SECTION.INFRASTRUCTURE]
// costs by how they're used: the work modelled, capacity left unused (a warning amber) and work outside the models (grey)
const ALLOCATION_COLOURS = { allocated: '#0f9d8a', unallocated: '#E8A400', unmodelled: '#8a93a6' }

class SectionField {
    constructor(public field:INSIGHTS_FIELD, public title:string, public subTitle:string, public colour:string, public includePercentages:boolean, public total:number, public rows:InsightsRow[]) {}
}

class Section {
    nodeType:NODE_TYPE
    description:string[] = []
    comment:string
    fields:SectionField[] = []
    cards:InsightCard[] = []
    visibleCards:InsightCard[] = []     // cards after optionally hiding zero values
    bubbleData: {heading:string, maxValue:number, labels:string[], data:BubbleDataSet[]}[] = []
    pieData: {heading:string, data:LinqChartData[], currency:boolean}[] = []
    treemaps: {heading:string, fieldType:INSIGHTS_FIELD, data1:LinqChartData[], data2:LinqChartData[], currency:boolean}[] = []

    constructor(public type:INSIGHTS_SECTION, public title:string, public colour:string, public hoverColour:string) {
    }

    get htmlComment() {
        return this.comment?this.comment.replace(/\n/g, '<br>'):''
    }
}

class TopFiveItem {
    items:number = 0
    hours:number = 0
    cost:number = 0

    constructor(public _key:string, public name:string, public description:string, public insights:NodeInsights|ModelInsights, public count?:number) {
        if (!insights) return

        const c = new InsightsFieldType(INSIGHTS_FIELD.COST, insights.modelKey, insights.projectKey)
        this.cost = c.getSingleValueFromInsights(insights, NODE_TYPE_DATA_TYPE.STANDARD)
        const h = new InsightsFieldType(INSIGHTS_FIELD.ANNUAL_HOURS, insights.modelKey, insights.projectKey)
        this.hours = h.getSingleValueFromInsights(insights, NODE_TYPE_DATA_TYPE.STANDARD)
        const i = new InsightsFieldType(INSIGHTS_FIELD.PROCESSED, insights.modelKey, insights.projectKey)
        this.items = i.getSingleValueFromInsights(insights, NODE_TYPE_DATA_TYPE.STANDARD)
    }
}

@Component({
    selector: 'app-insights-overview',
    templateUrl: './insights-overview.html',
    styleUrls: ['./insights-overview.scss'],
    standalone: false
})
export class InsightsOverviewComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() linqset:Linqset
    @Input() comparison:Linqset
    @Input() summary:boolean
    @Input() hideZeroCards:boolean = false     // hide cards with no value (and no comparison value) behind a "show" link

    @Input() graph:IGraph
    @Input() trigger:any

    modelType=MODEL_TYPE.OVERVIEW

    MODEL_COLOURS=MODEL_COLOURS
    INSIGHTS_SECTION=INSIGHTS_SECTION
    INSIGHTS_FIELD=INSIGHTS_FIELD
    filterOptions:INSIGHTS_FIELD[] = []
    sectionOptions:INSIGHTS_SECTION[] = []
    showAsTables:boolean = true
    includePercentages:boolean = true
    processingSubscription:Subscription
    userSubscription:Subscription

    insights:ModelInsights
    comparisonInsights:ModelInsights

    user:User

    sections:Section[] = []
    section:Section|undefined

    filters:INSIGHTS_FIELD[] = []
    overview:Section

    showGraph:boolean = true
    showZeroCards:boolean = false

    disableSave:boolean = true
    pdfMessage:string

    treemapFields = [INSIGHTS_FIELD.ANNUAL_HOURS, INSIGHTS_FIELD.COST, INSIGHTS_FIELD.CARBON]

    nodesWithMostHours:any[]

    // carousel
    carouselOpen = false;
    startAt = 0;
    slides: CarouselSlide[] = [];

    @ViewChild('slideTpl', { static: true }) slideTpl!: TemplateRef<any>;
    @ViewChild('epmSlideTpl', { static: true }) epmSlideTpl!: TemplateRef<any>;
    @ViewChild('recommendationsSlideTpl', { static: true }) recommendationsSlideTpl!: TemplateRef<any>;

    // the EPM summary and what to do first, worked out as the Overview does for the models looked at, once the carousel
    // opens (it takes a moment, so those slides say so until it's ready)
    epmOverview: {loading:boolean, error?:string, data?:OverviewData, recommendations:Recommendation[], scope?:AnalysisScope} = {loading:false, recommendations:[]}
    readonly leverLabels = LEVER_LABELS
    private overviewRequest = 0

    @Output() selectNodeEvent = new EventEmitter<string>()
    @Output() setupCompletedEvent = new EventEmitter<string>()

    constructor(private formatter:FormattingService, public app:AppComponent, private pdfService:PdfService, private tagService:TagsService,
        private dialog:MatDialog, private userService:UserService, private processingService:GraphProcessingService, private organisationService:OrganisationService,
        private host:ElementRef<HTMLElement>, private changeDetectorRef:ChangeDetectorRef, private overviewService:OverviewService,
        private scopeService:ScopeService, private router:Router) {}


    ngAfterViewInit(): void {
        if (!this.processingSubscription) {
            this.processingSubscription = this.processingService.processCompletedEvent.subscribe(async e => {
                this.setupData()
            })
        }

        this.user = this.userService.getUser()
        if (!this.user) return

        if (!this.userSubscription) {
            this.userSubscription = this.userService.userChangedEvent.subscribe(() => {
                this.user = this.userService.getUser()
                this.setupSectionsAndFields(this.linqset.filter)
                this.setupData()
            })
        }

        setTimeout(() => {
            this.setupSectionsAndFields(this.linqset.filter)
            this.setupData()
            this.setupCompletedEvent.emit('completed')
        })
    }

    ngOnDestroy(): void {
        if (this.processingSubscription) this.processingSubscription.unsubscribe()
        if (this.userSubscription) this.userSubscription.unsubscribe()
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (this.user && (changes["trigger"] || changes["linqset"])) {
            this.setupData()
        }
    }

    togglePercentages() {
        this.setupData()
    }

    getAllFilterOptions() {
        return Object.values(INSIGHTS_FIELD).filter(f => {
            return ignoreTypes.indexOf(f) < 0
        })
    }

    // each section a slide, named and coloured as it is on the page; then the EPM summary after the overview, and what
    // to do first at the end
    createSlides() {
        const sections:CarouselSlide[] = this.sections.map(s => ({template: this.slideTpl, data: s, title: s.title, colour: s.colour}))
        const epm:CarouselSlide = {template: this.epmSlideTpl, title: 'EPM summary', colour: MODEL_COLOURS.MODEL_COLOR}
        const first = sections.findIndex(s => s.data?.type === INSIGHTS_SECTION.OVERVIEW)
        sections.splice(first >= 0 ? first + 1 : 0, 0, epm)
        this.slides = [...sections, {template: this.recommendationsSlideTpl, title: 'What to do first', colour: MODEL_COLOURS.RED}]
    }

    // the EPM scorecard and recommendations for the models looked at, as the Overview works them out
    private async loadOverview() {
        const organisation = this.linqset?.organisation
        if (!organisation) return
        const request = ++this.overviewRequest
        const scope = scopeFromFilter(this.linqset.filter)
        this.epmOverview = {loading: true, recommendations: [], scope}
        try {
            const data = await this.overviewService.load(organisation, scope)
            if (request !== this.overviewRequest) return
            // at most three to act on now, so the largest of the rest show too
            this.epmOverview = {loading: false, data, scope, recommendations: previewRecommendations(data.recommendations, 6, 3)}
        }catch(err) {
            if (request !== this.overviewRequest) return
            console.error('Unable to build the overview for the carousel', err)
            this.epmOverview = {loading: false, recommendations: [], scope, error: "The EPM summary and recommendations couldn't be worked out. Please try again."}
        }
        this.changeDetectorRef.markForCheck()
    }

    get moreRecommendations() {
        return Math.max(0, (this.epmOverview.data?.recommendations.length || 0) - this.epmOverview.recommendations.length)
    }

    // a recommendation's size: the hours and cost it is about
    recommendationSize(r:Recommendation) {
        const parts = []
        if (r.hours) parts.push(`${this.formatter.formatAbbreviatedNumber(r.hours)} hours a year`)
        if (r.cost) parts.push(this.formatter.formatAbbreviatedNumber(r.cost, getCurrency(this.linqset?.organisation?.currencyCode || DEFAULT_CURRENCY)))
        return parts.join(' · ')
    }

    // the models looked at, for the context graphs of the nodes in the top fives
    get contextScope():AnalysisScope|undefined {
        return this.linqset?.filter ? scopeFromFilter(this.linqset.filter) : undefined
    }

    // a section's rows are nodes (roles, systems, actions, outputs), not models, so each can show its context
    contextOrganisationFor(section:Section) {
        return section?.nodeType !== undefined && section.nodeType !== NODE_TYPE.MODEL ? this.linqset?.organisation : undefined
    }

    lensLabel(key:LensKey) {
        return LENSES.find(l => l.key === key)?.label || key
    }

    // leaves the carousel for the view that explains it, looking at the same models
    openLens(key:LensKey, focus?:string) {
        const organisation = this.linqset?.organisation
        const lens = LENSES.find(l => l.key === key)
        if (!organisation || !lens) return
        this.carouselOpen = false
        this.scopeService.open(organisation._key, this.epmOverview.scope)
        void this.router.navigate([lens.url(organisation._key)], focus ? {queryParams: {focus}} : {})
    }

    // what the slides are of: the workspace, what it is compared with, and the models or filter looked at
    get carouselSubtitle() {
        if (!this.linqset) return ''
        const workspace = `<b>${escapeHtml(this.linqset.workspaceName)}</b>${this.comparison ? ` compared with <b>${escapeHtml(this.comparison.workspaceName)}</b>` : ''}`
        const scope = (this.linqset.description || '').trim()
        return scope ? `${workspace} · ${scope}` : workspace
    }

    // the one thing to take from a section: the largest by its first measure that has shares, the share it has, and
    // how much the top few make up between them. Nothing when the section has no shares to go on
    keyPoint(section:Section):string|undefined {
        const field = section.fields.find(f => f.includePercentages && f.rows.length > 1 && f.total > 0 && f.rows[0].percentage > 0)
        if (!field) return undefined
        const [top] = field.rows
        const measure = field.title.includes(' by ') ? field.title.slice(field.title.indexOf(' by ') + 4) : 'of the total'
        const topShare = Math.min(100, field.rows.reduce((t, r) => t + (r.percentage || 0), 0))
        return `<b>${top.name}</b> has the most ${measure}: <b>${top.percentage}%</b> of the total.` +
            (topShare > top.percentage && topShare < 100 ? ` The top ${field.rows.length} make up <b>${topShare}%</b>.` : '')
    }

    openCarousel() {
        this.createSlides()
        void this.loadOverview()
        this.startAt = 0;
        this.carouselOpen = true;
    }

    setupSectionsAndFields(filter:InsightsFilter) {
        if (filter?.sections && filter.sections.length > 0) {
            this.sectionOptions = filter.sections
        }else if (this.user.preferences.fieldTypes && this.user.preferences.fieldTypes.length > 0) {
            this.sectionOptions = this.user.preferences.sections
        }else this.sectionOptions = defaultSections

        if (filter?.fields && filter.fields.length > 0) {
            this.filterOptions = filter.fields
        }else if (this.user.preferences.fieldTypes && this.user.preferences.fieldTypes.length > 0) {
            this.filterOptions = this.user.preferences.fieldTypes
        }else this.filterOptions = defaultTypes

    }

    getSectionOptions() {
        return Object.values(INSIGHTS_SECTION)
    }

    async onSectionsClick(event:MatChipListboxChange) {
        this.sectionOptions = event.value
        this.setupData()
        this.disableSave = false
    }

    gotoSection(type:NODE_TYPE) {
        const s = this.sections.find(s => s.nodeType === type)
        if (s) {
            this.section = s
            if (!this.summary) {
                const el = document.getElementById(`section-${s.type}`);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }

    setupData() {
        if (!this.linqset?.insights) return

        this.insights = this.linqset?.insights
        this.comparisonInsights = this.comparison?.insights

        this.filters = this.filterOptions.filter(o => {
            // chosen as value and revenue (an older saved choice may still hold them)
            if (o === INSIGHTS_FIELD.VALUE_CONTRIBUTION || o === INSIGHTS_FIELD.REVENUE_CONTRIBUTION) return false
            if (o === INSIGHTS_FIELD.CAPACITY || o === INSIGHTS_FIELD.WORKLOAD) {
                return this.linqset.insights.modelledCapacity > 0
            }
            return true
        })
        this.createSections()
        this.disableSave = true
        this.showGraph = this.isSectionSelected(INSIGHTS_SECTION.INPUTS) || this.isSectionSelected(INSIGHTS_SECTION.OUTPUTS)
    }

    createSections() {
        this.sections = this.sectionOptions.filter(o => {
                if (organisationSections.indexOf(o) >=0) return this.linqset.isOrganisationModel()
                if (o === INSIGHTS_SECTION.FILTER) return !this.linqset.isOrganisationModel()

                return true
            }).map(o => {
                const s = this.createSummary(o)
                if (s) {
                    if (this.linqset.filter.comments && this.linqset.filter.comments.length > 0) {
                        const commentObj = this.linqset.filter.comments.find(c => c.section === o)
                        if (commentObj) s.comment = commentObj.comment
                    }
                }
                return s
            }).filter(s => s !== undefined)

            this.section = this.sections.find(s => s.type === INSIGHTS_SECTION.OVERVIEW)||this.sections[0]
            this.updateVisibleCards()
    }

    private isZeroCard(card:InsightCard) {
        return card.text === null && !card.image && !card.value && !card.csValue
    }

    updateVisibleCards() {
        this.sections.forEach(s => {
            s.visibleCards = (this.hideZeroCards && !this.showZeroCards)?s.cards.filter(c => !this.isZeroCard(c)):s.cards
        })
    }

    hiddenCardCount(section:Section) {
        return this.hideZeroCards?section.cards.filter(c => this.isZeroCard(c)).length:0
    }

    toggleZeroCards() {
        this.showZeroCards = !this.showZeroCards
        this.updateVisibleCards()
    }

    isOrganisationInsights() {
        return this.linqset && this.linqset.type === LINQSET_TYPE.ORGANISATION
    }

    isSectionSelected(section:INSIGHTS_SECTION) {
        return this.sectionOptions.indexOf(section) >= 0
    }

    isFieldSelected(field:INSIGHTS_FIELD) {
        return this.filterOptions.indexOf(field) >= 0
    }

    getComparisonColour(hexcolour:string) {
        const rgb = colorAsARGB(hexcolour)
        return `rgba(${rgb.r},${rgb.g},${rgb.b},0.4)`
    }


    getCostsForChart():LinqChartData[] {
        const pd:LinqChartData[] = []

        const i = this.linqset.insights
        const ci = this.comparisonInsights

        if (getCostsForType(i, NODE_SUPERTYPE.INFRASTRUCTURE))
            pd.push(new LinqChartData(getSuperTypeName(NODE_SUPERTYPE.INFRASTRUCTURE), getCostsForType(i, NODE_SUPERTYPE.INFRASTRUCTURE), MODEL_COLOURS.INFRASTRUCTURE_COLOR, ci && getCostsForType(ci, NODE_SUPERTYPE.INFRASTRUCTURE)))

        if (getCostsForType(i, NODE_SUPERTYPE.PEOPLE))
            pd.push(new LinqChartData(getSuperTypeName(NODE_SUPERTYPE.PEOPLE), getCostsForType(i, NODE_SUPERTYPE.PEOPLE), MODEL_COLOURS.PEOPLE_COLOR, ci && getCostsForType(ci, NODE_SUPERTYPE.PEOPLE)))
        if (getCostsForType(i, NODE_SUPERTYPE.ROLE))
            pd.push(new LinqChartData(getSuperTypeName(NODE_SUPERTYPE.ROLE), getCostsForType(i, NODE_SUPERTYPE.ROLE), MODEL_COLOURS.ROLE_COLOR, ci && getCostsForType(ci, NODE_SUPERTYPE.ROLE)))
        if (getCostsForType(i, NODE_SUPERTYPE.SYSTEM))
            pd.push(new LinqChartData(getSuperTypeName(NODE_SUPERTYPE.SYSTEM), getCostsForType(i, NODE_SUPERTYPE.SYSTEM), MODEL_COLOURS.SYSTEM_COLOR, ci && getCostsForType(ci, NODE_SUPERTYPE.SYSTEM)))
        return pd
    }

    // the organisation has some carbon allocated, unallocated or unmodelled, in this workspace or the one compared with
    hasCarbon() {
        const some = (i?:ModelInsights) => {
            if (!i?.emissions?.length) return false
            const t = sumAllocations(i.emissions)
            return t.allocated + t.unallocated + t.unmodelled > 0
        }
        return some(this.insights) || some(this.comparisonInsights)
    }

    // the organisation's costs as allocated (time spent on actions), unallocated (capacity not used) and unmodelled (work
    // not modelled in LINQ), leaving out any that are nothing
    getCostAllocationsForChart():LinqChartData[] {
        const i = sumAllocations(this.linqset.insights?.costAllocations || [])
        const c = this.comparisonInsights?.costAllocations && sumAllocations(this.comparisonInsights.costAllocations)
        return [
            new LinqChartData("Allocated", i.allocated, ALLOCATION_COLOURS.allocated, c?.allocated),
            new LinqChartData("Unallocated", i.unallocated, ALLOCATION_COLOURS.unallocated, c?.unallocated),
            new LinqChartData("Unmodelled", i.unmodelled, ALLOCATION_COLOURS.unmodelled, c?.unmodelled),
        ].filter(d => d.value > 0)
    }

    getActionTypeDataForChart() {

        const i = this.linqset.insights
        const ci = this.comparisonInsights

        const costs:LinqChartData[] = []
        const num:LinqChartData[] = []

        let totalCost = 0, totalNum = 0
        Object.keys(i.actionTypes).forEach(t => {
            const type = parseInt(t) as ACTION_TYPE
            num.push(new LinqChartData(getActionTypeName(type), i.actionTypes[type].number, getActionTypeColour(type), ci?.actionTypes[type].number))
            costs.push(new LinqChartData(getActionTypeName(type), i.actionTypes[type].cost, getActionTypeColour(type), ci?.actionTypes[type].cost))

            totalCost += i.actionTypes[type].cost
            totalNum += i.actionTypes[type].number
        })

        return {totalCost, totalNum, costs, num}
    }

    createSummary(t:INSIGHTS_SECTION) {
        switch (t) {
            case INSIGHTS_SECTION.FILTER:
                return this.filterSection()
            case INSIGHTS_SECTION.OVERVIEW:
                return this.overviewSection()
            case INSIGHTS_SECTION.WORKSPACES:
                return this.workspacesSection()
            case INSIGHTS_SECTION.MODEL_FUNCTIONS:
                return this.modelFunctionsSection()
            case INSIGHTS_SECTION.MODEL_TAGS:
                return this.modelTagsSection()
            case INSIGHTS_SECTION.MODELS:
                return this.modelsSection()
            case INSIGHTS_SECTION.ACTIONS:
                return this.actionsSection()
            case INSIGHTS_SECTION.INPUTS:
                return this.inputsSection()
            case INSIGHTS_SECTION.OUTPUTS:
                return this.outputsSection()
            case INSIGHTS_SECTION.OUTCOMES:
                return this.outcomesSection()
            case INSIGHTS_SECTION.INFO_ASSETS:
                return this.informationAssetsSection()
            case INSIGHTS_SECTION.ROLES:
                return this.rolesSection()
            case INSIGHTS_SECTION.SYSTEMS:
                return this.systemsSection()
            case INSIGHTS_SECTION.INFRASTRUCTURE:
                return this.infrastructureSection()
            case INSIGHTS_SECTION.PEOPLE:
                return this.peopleSection()
            case INSIGHTS_SECTION.EXTERNAL_ORGANISATIONS:
                return this.externalOrganisationsSection()
        }
    }

    getStartingMessage(number:number, name:string, capital:boolean=true) {
        const t = capital?'T':'t'
        if (number===0) return `${t}here are no <b>${name}s</b>`
        if (number===1) return `${t}here is one <b>${name}</b>`
        return `${t}here are <b>${this.formatter.formatNumber(number)} ${name}s</b>`
    }

    percentageMsg(before:number, after:number, label:string) {
        const chg = before > 0?Math.round(100*((after-before)/before)):undefined

        if (chg === 0) return `the same number of ${label}.`
        if (!chg) return `<b>${this.formatter.formatNumber(before)}</b> ${label}`

        if (chg > 0) return `<b>${this.formatter.formatNumber(before)}</b> ${label}, an increase of ${chg}%`
        if (chg < 0) return `<b>${this.formatter.formatNumber(before)}</b> ${label}, a decrease of ${chg}%`
        return ''
    }

    changeMessage(before:number, after:number, isCurrency:boolean=false) {
        const change = after-before
        const percentage = before===0?100:Math.abs(Math.round(100*change/before))

        if (change === 0) return `<span style="color:orange">no change</span>`
        if (change > 0) return `a <span style="color:green"><b>${percentage}% (${isCurrency?this.formatter.formatCost(change):this.formatter.formatNumber(change)})</b></span> increase`
        if (change < 0) return `a <span style="color:red"><b>${percentage}% (${isCurrency?this.formatter.formatCost(Math.abs(change)):this.formatter.formatNumber(Math.abs(change))})</b></span> decrease`
        return ''
    }

    getMessage(name:string, num:number, hours:number, cost:number, compareHours?:number, compareCost?:number) {
        let msg = `${this.getStartingMessage(num, name)}, working <b>${this.formatter.formatNumber(hours)}</b> hours. `

        if (this.comparisonInsights) msg += `There is ${this.changeMessage(compareHours, hours)} in hours compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>.`

        msg += ` Total ${name} costs are <b>${this.formatter.formatCost(cost)}</b>. `
        if (this.comparisonInsights) {
            msg += `There is ${this.changeMessage(compareCost, cost)} in costs compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>.`
        }

        return msg
    }

    getProcessedMessage(name:string, num:number, processed:number, compareProcessed?:number) {
        let msg = `${this.getStartingMessage(num, name)} processing <b>${this.formatter.formatNumber(processed)}</b> inputs per annum. `
        if (compareProcessed) msg += `There is ${this.changeMessage(compareProcessed, processed)} in number processed compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>.`
        return msg
    }

    filterSection() {
        //if (this.linqset.type === LINQSET_TYPE.MODEL) return undefined

        const s = new Section(INSIGHTS_SECTION.FILTER, INSIGHTS_SECTION.FILTER, MODEL_COLOURS.MODEL_COLOR, MODEL_COLOURS.MODEL_HOVER_COLOR)
        s.description.push(`${this.linqset.description}`)

        if (this.linqset.filter.modelKeys.length > 0) {
            this.linqset.models.forEach(m => {
                s.cards.push(new InsightCard("Model", NODE_TYPE.MODEL, "Model selected as filter", m.name))
            })
        }

        this.linqset.modelFunctions.forEach(f => {
            s.cards.push(new InsightCard("Model function", NODE_TYPE.MODEL, "Functional category within the business", f.toString()))
        })

        this.linqset.modelTags.forEach(t => {
            const c = new InsightCard("Model tag", NODE_TYPE.MODEL, "Model tag for grouping models", t.name)
            c.image = this.tagService.getIconForTag(t)
            s.cards.push(c)
        })

        this.linqset.nodeTags.forEach(t => {
            const c = new InsightCard("Node tag", NODE_TYPE.ACTION, "Node tag for grouping nodes", t.name)
            c.image = this.tagService.getIconForTag(t)
            s.cards.push(c)
        })

        this.linqset.filterNodes.forEach(n => {
            s.cards.push(new InsightCard("Node", n.type, "Node selected as filter", n.name))
        })

        this.linqset.informationAssets.forEach(ia => {
            s.cards.push(new InsightCard("Information asset", NODE_TYPE.ASSET, "Information asset selected as filter", ia.name))
        })
        return s
    }

    overviewSection() {
        const s = new Section(INSIGHTS_SECTION.OVERVIEW, INSIGHTS_SECTION.OVERVIEW, MODEL_COLOURS.MODEL_COLOR, MODEL_COLOURS.MODEL_HOVER_COLOR)
        s.description.push(`${this.linqset.description} in ${this.linqset.isProjectModel()?'the project ':''}<b>${escapeHtml(this.linqset.workspaceName)}</b>.${this.linqset.isProjectModel()?' It is being compared to current state.':''}`)

        const m1 = `${this.getStartingMessage(this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.number, "action")} performed by <b>${this.formatter.formatNumber(this.insights.superTypes[NODE_SUPERTYPE.ROLE]?.number)} role${this.insights.superTypes[NODE_SUPERTYPE.ROLE]?.number===1?'':'s'}</b> using <b>${this.insights.superTypes[NODE_SUPERTYPE.SYSTEM]?.number} system${this.insights.superTypes[NODE_SUPERTYPE.SYSTEM]?.number===1?'':'s'}</b>.`
        if (this.comparisonInsights) {
            s.description.push(`${m1} There is ${this.changeMessage(this.comparisonInsights.superTypes[NODE_SUPERTYPE.ACTION]?.number, this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.number)} in number of actions from <b>current state</b>.`)
        }else s.description.push(m1)

        const m2 = `There is a total of <b>${this.formatter.formatNumber(this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.hours)} hours</b> modelled with an estimated cost of <b>${this.formatter.formatCost(getTotalModelCost(this.insights))}</b>. This is the time taken to execute all the actions.`
        if (this.comparisonInsights) {
            s.description.push(`${m2} This is ${this.changeMessage(this.comparisonInsights.superTypes[NODE_SUPERTYPE.ACTION]?.hours, this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.hours)} in hours and ${this.changeMessage(getTotalModelCost(this.comparisonInsights), getTotalModelCost(this.insights), true)} in cost from <b>current state</b>.`)
        }else s.description.push(m2)

        if (this.isFieldSelected(INSIGHTS_FIELD.WORKLOAD) && this.insights.modelledCapacity) {
            const remaining = this.insights.modelledCapacity-this.insights.superTypes[NODE_SUPERTYPE.ROLE].hours
            let m = `Role capacity available is <b>${this.formatter.formatNumber(this.insights.modelledCapacity)} hours</b>. Role hours worked is <b>${this.formatter.formatNumber(this.insights.superTypes[NODE_SUPERTYPE.ROLE].hours)}</b> hours and so capacity remaining after removing modelled work is <span style='color:${remaining<0?'red':'green'};'><b>${this.formatter.formatNumber(remaining)} hours</b></span>.`

            m += ` This indicates that there is${remaining>=0?'':' not'} enough capacity available to do the work required.`
            s.description.push(m)
            s.description.push('Hours worked may differ from action hours in that it describes hours people worked on actions, while action time is elapsed time to do the work.')
        }

        if (this.linqset.type === LINQSET_TYPE.MODEL && this.linqset.organisation.modelTags) {
            (this.linqset.model.tagKeys || []).forEach(tk => {
                const tag = this.linqset.organisation.modelTags.tags.find((t:Tag) => t._key === tk)

                if (tag) {
                    const c = new InsightCard("Model tag", NODE_TYPE.MODEL, "Model tag for grouping models", tag.name)
                    c.image = this.tagService.getIconForTag(tag)
                    s.cards.push(c)
                }
            })
        }

        const outcomes = `<b>${this.insights.superTypes[NODE_SUPERTYPE.OUTCOMES]?.number}</b> business outcome${this.insights.superTypes[NODE_SUPERTYPE.OUTCOMES]?.number===1?' has ':'s have'} been recorded.`

        if (this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.number) {
            const p = Math.round(100*this.insights.actionTypes[ACTION_TYPE.AUTOMATED].number/this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.number)
            const cp = this.comparisonInsights?.superTypes[NODE_SUPERTYPE.ACTION]?.number && Math.round(100*this.comparisonInsights?.actionTypes[ACTION_TYPE.AUTOMATED].number/this.comparisonInsights?.superTypes[NODE_SUPERTYPE.ACTION].number)
            s.cards.push(new InsightCard("Automated actions", NODE_TYPE.ACTION, "Proportion of actions that are automated", undefined, p, cp, INSIGHT_VALUE_TYPE.PERCENTAGE))

            s.description.push(`<b>${this.insights.actionTypes[ACTION_TYPE.AUTOMATED].number}</b> of <b>${this.formatter.formatNumber(this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.number)}</b> or <b>${p}%</b> of actions are automated. ${outcomes}`)
        }else s.description.push(outcomes)

        s.description.push("Costs are broken down into allocated, unallocated and unmodelled. <b>Allocated</b> are from time spent performing actions, <b>unallocated</b> are from unused capacity and so cannot be assigned and <b>unmodelled</b> are for work that has not been modelled in LINQ.")

        if (this.linqset.isOrganisationModel()) {
            s.description.push("This is a view of the whole organisation and so the unallocated and unmodelled costs are shown.")
        }else {
            s.description.push("This is a view of a model or subset of models within and so only allocated costs are shown. Unallocated and unmodelled costs cannot be assigned to models and so are only shown at the organisation level.")
        }
        this.filters.forEach(o => {
            if (o === INSIGHTS_FIELD.PROCESSED) {
                if (this.linqset.hasModelNodes)
                    s.cards.push(new InsightCard("Internal inputs received", NODE_TYPE.INFORMATION, "Number of information inputs received from other models", undefined, this.insights.numInputsProcessed-this.insights.numInputsExternal, this.comparisonInsights?.numInputsProcessed-this.comparisonInsights?.numInputsExternal, INSIGHT_VALUE_TYPE.NUMBER))

                s.cards.push(new InsightCard("External inputs received", NODE_TYPE.INFORMATION, "Number of information inputs received from external sources", undefined, this.insights.numInputsExternal, this.comparisonInsights?.numInputsExternal, INSIGHT_VALUE_TYPE.NUMBER))

                s.cards.push(new InsightCard("Outputs produced", NODE_TYPE.INFORMATION, "Outcomes reached, and outputs at the end of a chain that reach no outcome", undefined, this.insights.numOutputsProcessed||0, this.comparisonInsights?.numOutputsProcessed, INSIGHT_VALUE_TYPE.NUMBER))
            }else if (o === INSIGHTS_FIELD.ANNUAL_HOURS) {
                const c = new InsightCard("Action time", NODE_TYPE.ACTION, "Total hours of work", undefined, this.insights.superTypes[NODE_SUPERTYPE.ACTION]?.hours||0, this.comparisonInsights?.superTypes[NODE_SUPERTYPE.ACTION]?.hours, INSIGHT_VALUE_TYPE.NUMBER)
                c.units = "hours"
                s.cards.push(c)
                const r = new InsightCard("Role time", NODE_TYPE.ROLE, "Time the organisation's people spent doing work, leaving out external organisations", undefined, this.insights.superTypes[NODE_SUPERTYPE.ROLE]?.hours||0, this.comparisonInsights?.superTypes[NODE_SUPERTYPE.ROLE]?.hours, INSIGHT_VALUE_TYPE.NUMBER)
                r.units = "hours"
                s.cards.push(r)
                // external organisations' time, reported apart from the roles'
                if (this.insights.externalOrgs?.hours || this.comparisonInsights?.externalOrgs?.hours) {
                    const e = new InsightCard("External organisation time", NODE_TYPE.EXTERNAL_ORGANISATION, "Time external organisations spent on the work", undefined, this.insights.externalOrgs?.hours||0, this.comparisonInsights?.externalOrgs?.hours, INSIGHT_VALUE_TYPE.NUMBER)
                    e.units = "hours"
                    s.cards.push(e)
                }

            }else if ( o === INSIGHTS_FIELD.COST) {
                s.cards.push(new InsightCard("People cost", NODE_TYPE.ROLE, "Cost of people and roles", undefined, getPeopleRoleModelCost(this.insights), this.comparisonInsights && getPeopleRoleModelCost(this.comparisonInsights), INSIGHT_VALUE_TYPE.CURRENCY))
                s.cards.push(new InsightCard("System cost", NODE_TYPE.SYSTEM, "Cost of systems and infrastructure", undefined, getSystemInfrastructureModelCost(this.insights), this.comparisonInsights && getSystemInfrastructureModelCost(this.comparisonInsights), INSIGHT_VALUE_TYPE.CURRENCY))

                if (this.linqset.isOrganisationModel() && !this.linqset.isProjectModel()) {
                    // current state. come up with totals saved
                    const totals = {cost:getTotalModelCost(this.insights), hours:this.insights.superTypes[NODE_SUPERTYPE.ACTION].hours}

                    const totalSaved = this.linqset.organisation.model.insights.reduce((t, i) => {
                        t.cost += totals.cost - getTotalModelCost(i)
                        t.hours += totals.hours - i.superTypes[NODE_SUPERTYPE.ACTION].hours
                        return t
                    }, {cost:0, hours:0})
                    s.cards.push(new InsightCard("Total hours saved in projects", NODE_TYPE.ACTION, "Total hours saved in projects", undefined, totalSaved.hours))
                    s.cards.push(new InsightCard("Total cost saved in projects", NODE_TYPE.ACTION, "Total amount saved in projects", undefined, totalSaved.cost, undefined, INSIGHT_VALUE_TYPE.CURRENCY))
                }
            }else if (o !== INSIGHTS_FIELD.AUTOMATED_ACTIONS && o !== INSIGHTS_FIELD.ACTIONS_SUPPORTED) {
                const ft = new InsightsFieldType(o, this.linqset.modelKey, this.linqset.workspaceKey, this.comparisonInsights?.projectKey)
                const vt = ft.isCurrency()?INSIGHT_VALUE_TYPE.CURRENCY:INSIGHT_VALUE_TYPE.NUMBER
                const c = new InsightCard(ft.type.toString(), ft.getNodeType(), ft.getDescription(), undefined, ft.getSingleValueFromInsights(this.insights, NODE_TYPE_DATA_TYPE.MODELS), this.comparisonInsights && ft.getSingleValueFromInsights(this.comparisonInsights, NODE_TYPE_DATA_TYPE.MODELS), vt)
                c.units = ft.getUnits()
                s.cards.push(c)
            }
        })

        if (this.isFieldSelected(INSIGHTS_FIELD.COST)) {
            const costData = this.getCostsForChart()
            if (costData.length > 0) s.pieData = [{heading:this.isOrganisationInsights()?"Total modelled costs":"Allocated costs", data:costData, currency:true}]
            // the whole organisation's costs by how they're used: only it has the unallocated and unmodelled costs
            const allocationData = this.isOrganisationInsights() ? this.getCostAllocationsForChart() : []
            if (allocationData.length > 0) s.pieData.push({heading:"How costs are used", data:allocationData, currency:true})
        }

        if (this.isFieldSelected(INSIGHTS_FIELD.VALUE)) {
            // friction: the dead ends (outputs nothing uses, with no revenue or value) and the exception work (actions on a
            // detour from the main flow, or marked as such)
            s.cards.push(new InsightCard("Dead-end time", NODE_TYPE.INFORMATION, "Time to produce outputs nothing uses, that have no revenue or value", undefined, this.insights.frictionTime, this.comparisonInsights?.frictionTime))
            s.cards.push(new InsightCard("Exception work time", NODE_TYPE.ACTION, "Time on actions that are exception work: on a detour from the main flow, or marked as such", undefined, this.insights.exceptionTime || 0, this.comparisonInsights && (this.comparisonInsights.exceptionTime || 0)))
            if (this.isFieldSelected(INSIGHTS_FIELD.COST)) {
                s.cards.push(new InsightCard("Cost of dead ends", NODE_TYPE.INFORMATION, "Cost of outputs nothing uses, that have no revenue or value", undefined, this.insights.frictionCost, this.comparisonInsights?.frictionCost, INSIGHT_VALUE_TYPE.CURRENCY))
                s.cards.push(new InsightCard("Cost of exception work", NODE_TYPE.ACTION, "Cost of actions that are exception work: on a detour from the main flow, or marked as such", undefined, this.insights.exceptionCost || 0, this.comparisonInsights && (this.comparisonInsights.exceptionCost || 0), INSIGHT_VALUE_TYPE.CURRENCY))
                if (this.insights.wasteCost || this.comparisonInsights?.wasteCost) s.cards.push(new InsightCard("Cost of work not adding value", NODE_TYPE.ACTION, "Cost of actions marked as not adding value: waste other than exception work", undefined, this.insights.wasteCost || 0, this.comparisonInsights && (this.comparisonInsights.wasteCost || 0), INSIGHT_VALUE_TYPE.CURRENCY))
                const friction = (i:ModelInsights) => i.frictionCost + (i.exceptionCost || 0) + (i.wasteCost || 0)
                s.cards.push(new InsightCard("Cost of friction", NODE_TYPE.INFORMATION, "Dead ends, exception work and work not adding value together, as the EPM scorecard counts them", undefined, friction(this.insights), this.comparisonInsights && friction(this.comparisonInsights), INSIGHT_VALUE_TYPE.CURRENCY))
            }

            if (this.isFieldSelected(INSIGHTS_FIELD.CARBON)) {
                s.cards.push(new InsightCard("Carbon from dead ends", NODE_TYPE.INFORMATION, "Carbon emissions of outputs nothing uses, that have no revenue or value", undefined, this.insights.frictionCarbon, this.comparisonInsights?.frictionCarbon))
                s.cards.push(new InsightCard("Carbon from exception work", NODE_TYPE.ACTION, "Carbon emissions of actions that are exception work", undefined, this.insights.exceptionCarbon || 0, this.comparisonInsights && (this.comparisonInsights.exceptionCarbon || 0)))
            }
        }

        this.overview = s

        return s
    }

    workspacesSection() {
        if (!this.linqset.isOrganisationModel() || this.linqset.isProjectModel()) return undefined

        // only available for organisation view
        const changeMap = {}
        const items = this.linqset.organisation.projects.filter(p => !p.sandbox && this.linqset.organisation.model.insights.find(i => i.projectKey === p._key)).map(p => {
            changeMap[p._key] = 0
            return new TopFiveItem(p._key, p.name, p.description, this.linqset.organisation.model.insights.find(i => i.projectKey === p._key))
        })
        if (items.length > 0) {
            const sandboxes = this.linqset.organisation.projects.length-items.length

            items.push(new TopFiveItem(CURRENT_STATE_PROJECT_KEY, "Current state", "Represents the current state of the organisation", this.linqset.organisation.model.insights.find(i => i.projectKey === CURRENT_STATE_PROJECT_KEY)))

            // models changed in each project (sandboxes aren't listed)
            this.linqset.organisation.models.filter(m => !m.deletedDate).forEach(m => {
                (m.projects || []).forEach(p => {
                    if (changeMap[p] !== undefined) changeMap[p]++
                })
            })
            items.forEach(i => i.count = changeMap[i._key])

            const s = new Section(INSIGHTS_SECTION.WORKSPACES, INSIGHTS_SECTION.WORKSPACES.toString(), MODEL_COLOURS.PROJECT_MODEL, MODEL_COLOURS.PROJECT_MODEL)

            s.description.push(`${this.getStartingMessage(this.linqset.organisation.projects.length, "project")} in the organisation. ${sandboxes?`<b>${sandboxes}</b> of which are sandbox projects.`:''}`)

            s.fields = this.setTopFive(this.linqset, s, items, this.filters, "Workspaces", NODE_TYPE_DATA_TYPE.MODELS)

            return s
        }
        return undefined
    }

    modelFunctionsSection() {
        const s = new Section(INSIGHTS_SECTION.MODEL_FUNCTIONS, "Model functions", MODEL_COLOURS.MODEL_COLOR, MODEL_COLOURS.MODEL_HOVER_COLOR)
        s.nodeType = NODE_TYPE.MODEL

        const functionMap = this.linqset.models.reduce((a, m) => {
            // models without a function aren't part of any function's totals
            if (!m.function) return a
            if (!a[m.function]) a[m.function] = []
            a[m.function].push(m)
            return a
        }, {})

        const functions = Object.keys(functionMap)

        s.description.push(`${this.getStartingMessage(functions.length, "model function")}`)

        const items = functions.map(f => {
            const filter = createInsightsFilter(this.linqset.workspaceKey)
            filter.modelFunctions = [f as MODEL_FUNCTION]
            filter.workspaceKey = this.linqset.workspaceKey

            const newLinqset = createLinqset(this.linqset.organisation, filter)
            return new TopFiveItem(f, f, undefined, totalModelInsights(newLinqset.models, newLinqset.workspaceKey, newLinqset.organisationKey, newLinqset.nodes), functionMap[f].length)
        })

        s.fields = this.setTopFive(this.linqset, s, items, this.filters, "Model functions", NODE_TYPE_DATA_TYPE.MODELS)

        return s
    }

    modelTagsSection() {
        if (this.linqset.organisation.modelTags === undefined || this.linqset.organisation.modelTags.tags.length===0) return undefined

        let count = 0

        const tagMap = this.linqset.models.reduce((a, m) => {
            if (m.tagKeys?.length > 0) {
                count++
                m.tagKeys.forEach(tk => {
                    if (this.linqset.organisation.modelTags.tags.find(t => t._key === tk)) {
                        if (!a[tk]) a[tk] = []
                        a[tk].push(m)
                    }
                })
            }else {
                if (!a["notags"]) a["notags"] = []
                a["notags"].push(m)
            }
            return a
        }, {})

        if (Object.keys(tagMap).length === 0) return undefined

        const s = new Section(INSIGHTS_SECTION.MODEL_TAGS, "Model tags", MODEL_COLOURS.MODEL_COLOR, MODEL_COLOURS.MODEL_HOVER_COLOR)
        s.nodeType = NODE_TYPE.MODEL

        s.description.push(`${this.getStartingMessage(this.linqset.organisation.modelTags.tags.length, "model tag")}. <b>${count}</b> of <b>${this.linqset.models.length}</b> models in this selection have model tags.`)
        s.description.push(`Some models may have more than one tag and so the numbers may not add up to the totals for these insights.`)

        const items = Object.keys(tagMap).map(k => {
            const filter = createInsightsFilter(this.linqset.workspaceKey)
            filter.modelTags = [k]
            filter.workspaceKey = this.linqset.workspaceKey
            const newLinqset = createLinqset(this.linqset.organisation, filter)
            if (k === "notags") {
                return new TopFiveItem(k, "No tags", undefined, totalModelInsights(newLinqset.models, newLinqset.workspaceKey, newLinqset.organisationKey, newLinqset.nodes), tagMap[k].length)
            }else {
                const tag = this.linqset.organisation.modelTags.tags.find(t => t._key === k)
                return new TopFiveItem(k, tag.name, undefined, totalModelInsights(newLinqset.models, newLinqset.workspaceKey, newLinqset.organisationKey, newLinqset.nodes), tagMap[k].length)
            }
        })

        s.fields = this.setTopFive(this.linqset, s, items, this.filters, "Model tags", NODE_TYPE_DATA_TYPE.MODELS)

        return s
    }

    modelsSection() {
        if (this.linqset.models.length === 0) return undefined

        const models = this.linqset.models.map(m => this.createTopFiveItemFromModel(m))
        if (models.every(m => !m.hours)) return undefined

        const s = new Section(INSIGHTS_SECTION.MODELS, "Models", MODEL_COLOURS.MODEL_COLOR, MODEL_COLOURS.MODEL_HOVER_COLOR)
        s.nodeType = NODE_TYPE.MODEL
        if (models.length > 0) {
            s.description.push(this.getProcessedMessage('model', models.length, this.insights.numInputsProcessed, this.comparisonInsights?.numInputsProcessed))

            s.fields = this.setTopFive(this.linqset, s, models, this.filters, "Models", NODE_TYPE_DATA_TYPE.MODELS)
        }

        if (this.isOrganisationInsights() && (this.linqset.type === LINQSET_TYPE.MODEL || this.linqset.type === LINQSET_TYPE.ORGANISATION || this.linqset.type === LINQSET_TYPE.MODEL_FILTER)) {
            const models1 = getUnsortedModelList(this.organisationService.organisation, this.linqset.project)
            const models2 = this.comparison && getUnsortedModelList(this.organisationService.organisation, this.comparison.project)

            if (models1.length > 1) {
                s.treemaps = this.treemapFields.filter(f => this.isFieldSelected(f)).map(f => {
                    return {heading:"Models by " + f.toString().toLowerCase(), fieldType:f, data1:[], data2:[], currency:false}
                })
                if (s.treemaps.length > 0) {
                    s.treemaps.forEach(tm => {
                        const f = new InsightsFieldType(tm.fieldType, this.linqset.modelKey, this.linqset.workspaceKey)
                        tm.currency = f.isCurrency()

                        models1.forEach(m => {
                            const insights = m.insights?.find(i => i.projectKey === this.linqset.workspaceKey)
                            tm.data1.push(new LinqChartData(m.name, f.getSingleValueFromInsights(insights, NODE_TYPE_DATA_TYPE.MODELS)))
                        })
                        if (this.comparison && models2) {
                            models2.forEach(m => {
                                const insights = m.insights?.find(i => i.projectKey === this.comparison.workspaceKey)
                                tm.data2.push(new LinqChartData(m.name, f.getSingleValueFromInsights(insights, NODE_TYPE_DATA_TYPE.MODELS)))
                            })
                        }
                    })
                }
            }
        }

        return s
    }

    createTopFiveItemFromNode(node:LinqsetNode, linqset:Linqset):TopFiveItem {
        const insights = getInsights(node, linqset)
        const name = insights?.actionType === ACTION_TYPE.EXTERNAL?node.name  + '*':node.name
        return new TopFiveItem(node._key, name, node.description, insights)
    }

    createTopFiveItemFromModel(model:ModelDetails):TopFiveItem {
        return new TopFiveItem(model._key, model.name, model.description, model.insights?.find(i => i.projectKey === this.linqset.workspaceKey))
    }

    inputsSection():Section {
        if (this.linqset.nodes.length === 0) return undefined

        let numExternals = 0
        const inputs = this.linqset.nodes.filter(n => {
            if (!isModelNode(n.superType) || (!this.linqset.hasModelNodes && n.superType !== NODE_SUPERTYPE.ACTION)) return false

            const i = getInsightsForNode(n, n.modelKey, this.linqset.workspaceKey)
            if (!i) return false
            if (i.isInput && n.superType === NODE_SUPERTYPE.ACTION) {
                numExternals++
            }
            return i.isInput
        }).map(n => this.createTopFiveItemFromNode(n, this.linqset))

        if (inputs.length == 0) return undefined

        const s = new Section(INSIGHTS_SECTION.INPUTS, "Inputs", MODEL_COLOURS.ACTION_COLOR, MODEL_COLOURS.ACTION_HOVER_COLOR)
        s.nodeType = NODE_TYPE.CAPTURE
        s.description.push(`<b>External input nodes</b> define information coming into the organisation that needs processing. ${this.getProcessedMessage('external input node', numExternals, this.insights.numInputsExternal, this.comparisonInsights?.numInputsExternal)}`)
        if (this.linqset.hasModelNodes) {
            const n = this.insights.numInputsProcessed-this.insights.numInputsExternal
            if (n > 0) s.description.push(`This model receives information from other models within the organisation. ${this.getProcessedMessage('model input node', inputs.length-numExternals, n, this.comparisonInsights?.numInputsProcessed-this.comparisonInsights?.numInputsExternal)}`)
        }
        s.description.push("The numbers shown below are the totals to process the work for the input node listed.")
        s.fields = this.setTopFive(this.linqset, s, inputs, this.filters, "Input nodes", NODE_TYPE_DATA_TYPE.INPUT, NODE_SUPERTYPE.ACTION)

        return s
    }

    outputsSection():Section {
        let numWithValue = 0
        let numWithRevenue = 0
        const outputs = this.linqset.nodes.filter(n => {
            if (n.superType !== NODE_SUPERTYPE.INFORMATION || (this.linqset.type === LINQSET_TYPE.MODEL && n.modelKey !== this.linqset.modelKey)) return false
            const i = getInsightsForNode(n, n.modelKey, this.linqset.workspaceKey)
            if (!i) return false
            if (i.isOutput) {
                if (n.properties?.outputRevenue) numWithRevenue++
                if (n.properties?.outputValue) numWithValue++
                return true
            }
            return false
        }).map(n => this.createTopFiveItemFromNode(n, this.linqset))

        if (outputs.length == 0) return undefined

        const s = new Section(INSIGHTS_SECTION.OUTPUTS, "Outputs", MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR)
        s.nodeType = NODE_TYPE.INFORMATION
        s.description.push("<b>Output nodes</b> are the final information nodes on each branch of the model. They represent the outputs from the model.")
        s.description.push(`${this.getStartingMessage(outputs.length, "output node")} producing <b>${this.formatter.formatNumber(this.insights.numOutputsProcessed)}</b> outputs per annum.`)
        if (this.comparison) s.description.push(`There is ${this.changeMessage(this.comparisonInsights.numOutputsProcessed, this.insights.numOutputsProcessed)} in number of outputs compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>.`)
        if (this.isFieldSelected(INSIGHTS_FIELD.REVENUE) && numWithRevenue)
            s.description.push(`${this.getStartingMessage(numWithRevenue, "output node")} that ha${numWithRevenue===1?'s':'ve'} revenue, producing total revenue per annum of <b>${this.formatter.formatCost(this.insights.outputRevenue)}</b>. ${this.comparisonInsights?this.changeMessage(this.comparisonInsights.outputRevenue, this.insights.outputRevenue):''}`)

        if (this.isFieldSelected(INSIGHTS_FIELD.VALUE) && numWithValue) {
            s.description.push(`${this.getStartingMessage(numWithValue, "output node")} that ha${numWithValue===1?'s':'ve'} been given a value, producing <b>${this.formatter.formatNumber(this.insights.outputValue)}</b> of value (node output value multiplied by the number of items produced). ${this.comparisonInsights?this.changeMessage(this.comparisonInsights.outputValue, this.insights.outputValue):''}`)
        }

        s.fields = this.setTopFive(this.linqset, s, outputs, this.filters, "Output nodes", NODE_TYPE_DATA_TYPE.OUTPUT, NODE_SUPERTYPE.INFORMATION)

        s.description.push("The numbers shown below are the totals to produce the outputs listed.")

        return s
    }

    outcomesSection():Section {
        const outcomes = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.OUTCOMES).map(n => this.createTopFiveItemFromNode(n, this.linqset))

        if (outcomes.length == 0) return undefined

        const s = new Section(INSIGHTS_SECTION.OUTCOMES, "Outcomes", MODEL_COLOURS.OUTCOMES_COLOR, MODEL_COLOURS.OUTCOMES_HOVER_COLOR)
        s.nodeType = NODE_TYPE.OUTCOMES

        s.description.push("<b>Outcome nodes</b> are created at the end of a branch to denote an outcome for the business. They may bring together several outputs from the model.")

        s.fields = this.setTopFive(this.linqset, s, outcomes, this.filters, "Outcome nodes", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.OUTCOMES)

        s.description.push("The numbers shown below are the totals to produce the outcomes listed.")

        return s
    }

    informationAssetsSection():Section {
        if (this.insights.informationAssets.length === 0) return undefined

        const assets = []
        this.insights.informationAssets.forEach(a => {
            const asset = this.linqset.organisation.informationAssets?.find(ia => ia._key === a.groupKey)
            if (asset) {
                const node = createLinqsetNode(a.groupKey, NODE_SUPERTYPE.INFORMATION, NODE_TYPE.INFORMATION, this.linqset, asset.name)
                const insights = getInsights(node, this.linqset)
                insights.hours = a.hours
                insights.numberProcessed = a.numberProcessed
                insights.allocatedCostsByType = a.allocatedCostsByType
                insights.value = a.value
                assets.push(this.createTopFiveItemFromNode(node, this.linqset))
            }
        })

        const s = new Section(INSIGHTS_SECTION.INFO_ASSETS, "Information assets", MODEL_COLOURS.INFO_COLOR, MODEL_COLOURS.INFO_HOVER_COLOR)
        s.nodeType = NODE_TYPE.INFORMATION
        s.description.push("<b>Information assets</b> are the assets created or changed within this model.")
        if (assets.length > 0) {
            s.description.push(`${this.getStartingMessage(assets.length, "information asset")} and <b>${this.insights.superTypes[NODE_SUPERTYPE.INFORMATION].number}</b> information states (nodes).`)

            s.fields = this.setTopFive(this.linqset, s, assets, this.filters, "Assets", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.INFORMATION)
        }else {
            s.description.push("There are no information assets defined for this model.")
        }
        return s
    }

    actionsSection():Section {
        const s = new Section(INSIGHTS_SECTION.ACTIONS, "Actions", MODEL_COLOURS.ACTION_COLOR, MODEL_COLOURS.ACTION_HOVER_COLOR)
        s.nodeType = NODE_TYPE.ACTION
        s.description.push("<b>Actions</b> define the work done within the model. Actions may be performed by people (roles), systems or both. <b>Automated actions</b> are performed by systems only.")
        s.description.push(this.getMessage("action", this.insights.superTypes[NODE_SUPERTYPE.ACTION].number, this.insights.superTypes[NODE_SUPERTYPE.ACTION].hours, getTotalModelCost(this.insights), this.comparisonInsights?.superTypes[NODE_SUPERTYPE.ACTION].hours, this.comparison && getTotalModelCost(this.comparisonInsights)))

        if (this.linqset.hasModelNodes) {
            const actions = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => this.createTopFiveItemFromNode(n, this.linqset))

            if (actions.length > 0) {
                s.fields = this.setTopFive(this.linqset, s, actions, this.filters, "Actions", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.ACTION)
            }
        }

        if (this.insights.superTypes[NODE_SUPERTYPE.ACTION].number)
            s.description.push(`<b>${Math.round(100*this.insights.actionTypes[ACTION_TYPE.AUTOMATED].number/this.insights.superTypes[NODE_SUPERTYPE.ACTION].number)}%</b> of actions are automated. `)

        // exception work: friction, as the EPM scorecard counts it
        const exceptionActions = this.insights.exceptionActions || 0
        if (exceptionActions) {
            const cost = this.insights.exceptionCost || 0
            s.description.push(`<b>${this.formatter.formatNumber(exceptionActions)}</b> ${exceptionActions === 1 ? 'action is' : 'actions are'} <b>exception work</b> (on a detour from the main flow, or marked as such), taking <b>${this.formatter.formatNumber(this.insights.exceptionTime || 0)} hours</b>${cost ? ` and <b>${this.formatter.formatCost(cost)}</b>` : ''} a year: friction on the EPM scorecard. ${this.comparisonInsights ? this.changeMessage(this.comparisonInsights.exceptionTime || 0, this.insights.exceptionTime || 0) : ''}`)
        }

        if (this.comparisonInsights?.actionTypes) {
            if (this.comparisonInsights.actionTypes[ACTION_TYPE.AUTOMATED].number === this.insights.actionTypes[ACTION_TYPE.AUTOMATED].number && this.insights.actionTypes[ACTION_TYPE.AUTOMATED].processed)
                s.description.push(`In comparison, <b>${escapeHtml(this.comparison.workspaceName)}</b> has the same number of actions automated.`)
            else if (this.comparisonInsights.superTypes[NODE_SUPERTYPE.ACTION].number)
                s.description.push(`In comparison, In <b>${escapeHtml(this.comparison.workspaceName)}</b> <b>${Math.round(100*this.comparisonInsights.actionTypes[ACTION_TYPE.AUTOMATED].number/this.comparisonInsights.superTypes[NODE_SUPERTYPE.ACTION].number)}%</b> of actions is automated.`)
        }
        s.description.push(`The following table categorises actions by whether it is performed by people, systems or neither. Automated actions are systems only, while manual are people only.`)

        const data = this.getActionTypeDataForChart()

        if (data.totalNum > 0) {
            s.pieData.push({heading:"Action type numbers", data:data.num, currency:false})
        }

        if (this.isFieldSelected(INSIGHTS_FIELD.COST) && data.totalCost > 0) {
            s.pieData.push({heading:"Action type costs", data:data.costs, currency:false})
        }

        return s
    }

    rolesSection():Section {
        const roles = this.linqset.nodes.filter(n => n.type === NODE_TYPE.ROLE).map(n => this.createTopFiveItemFromNode(n, this.linqset))

        if (roles.length == 0) return undefined

        const s = new Section(INSIGHTS_SECTION.ROLES, "Internal roles", MODEL_COLOURS.ROLE_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR)
        s.nodeType = NODE_TYPE.ROLE

        s.description.push("<b>Roles</b> represent a set of skills or a position within the organisation. <b>People</b> can be assigned roles to distribute capacity and costs. <b>Role</b> capacity is compared to work to understand how people are aligned with work.")
        s.description.push("This section excludes external organisations from the insights shown. External organisation insights can be viewed in their own section.")

        // the roles' time and capacity leave out external organisations' (reported apart); their cost is taken off here
        if (roles.length > 0) {
            const hours = this.insights.superTypes[NODE_SUPERTYPE.ROLE].hours
            const costs = getPeopleRoleModelCost(this.insights)-(this.insights.externalOrgs?.allocatedCost||0)
            const cHours = this.comparisonInsights && this.comparisonInsights.superTypes[NODE_SUPERTYPE.ROLE].hours
            const cCosts = this.comparisonInsights && getPeopleRoleModelCost(this.comparisonInsights)-(this.comparisonInsights.externalOrgs?.allocatedCost||0)

            s.description.push(this.getMessage("role", roles.length, hours, costs, cHours, cCosts))

            if (this.filterOptions.indexOf(INSIGHTS_FIELD.CAPACITY) >= 0) {
                const capacity = this.insights.superTypes[NODE_SUPERTYPE.ROLE].capacity
                if (capacity > 0) {
                    const remaining = capacity-hours
                    const msg = remaining > 0?`This leaves <b>${this.formatter.formatNumber(remaining)}</b> hours of capacity available`:`<style='color:red;'> indicating ${this.getStartingMessage(Math.abs(remaining), "hour", false)} more work than capacity available.</style>`
                    s.description.push(`Capacity available to work is <b>${this.formatter.formatNumber(capacity)}</b> hours ${msg}`)

                    if (this.comparisonInsights) {
                        const cCapacity = this.comparisonInsights.superTypes[NODE_SUPERTYPE.ROLE].capacity
                        s.description.push(`There has been ${this.changeMessage(cCapacity, capacity)} in available capacity compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>`)

                        const compareRemaining = cCapacity-cHours
                        s.description.push(`There has been ${this.changeMessage(compareRemaining, remaining)} in remaining capacity compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>`)
                    }

                    if (this.insights.unusedCapacity > 0)
                        s.description.push(`Unused capacity is <b>${this.formatter.formatNumber(this.insights.unusedCapacity)}</b> hours. This indicates you have roles defined that are not assigned any work.`)

                    if (this.insights.unModelledCapacity > 0)
                        s.description.push(`Unmodelled capacity is <b>${this.formatter.formatNumber(this.insights.unModelledCapacity)}</b> hours. This is where you have roles/people with work that is not currently represented in LINQ.`)
                }else {
                    s.description.push(`No capacity is defined for this selection and so no capacity information will be shown. If you wish to model capacity you will need to set it on the roles/people doing the work.`)
                }
            }

            s.fields = this.setTopFive(this.linqset, s, roles, this.filters, "Roles", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.ROLE)

            const comparisonNodes = this.comparison?this.comparison.nodes.filter(n => n.type === NODE_TYPE.ROLE).map(n => this.createTopFiveItemFromNode(n, this.comparison)):undefined
            this.addBubbleCharts(s, roles, comparisonNodes)
        }

        return s
    }

    externalOrganisationsSection():Section {
        const orgs = this.linqset.nodes.filter(n => n.type === NODE_TYPE.EXTERNAL_ORGANISATION).map(n => this.createTopFiveItemFromNode(n, this.linqset))
        if (orgs.length === 0) return undefined

        const s = new Section(INSIGHTS_SECTION.EXTERNAL_ORGANISATIONS, "External roles", MODEL_COLOURS.ROLE_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR)
        s.nodeType = NODE_TYPE.EXTERNAL_ORGANISATION
        s.fields = this.setTopFive(this.linqset, s, orgs, this.filters, "External roles", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.ROLE)

        s.description.push(`External roles do work or provide information and are external to this organisation.`)
        s.description.push(this.getMessage("external role", orgs.length, this.insights.externalOrgs.hours, this.insights.externalOrgs.allocatedCost, this.comparisonInsights?.externalOrgs.hours, this.comparisonInsights?.externalOrgs.allocatedCost))
        return s
    }

    peopleSection():Section {
        let individualCount = 0
        let totalCount = 0
        const people = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.PEOPLE).map(n => {
            if (n.type === NODE_TYPE.PERSON) {
                individualCount++
                totalCount++
            }else {
                totalCount += n.properties.people||1
            }
            return this.createTopFiveItemFromNode(n, this.linqset)

        })
        if (people.length === 0) return undefined

        const s = new Section(INSIGHTS_SECTION.PEOPLE, "People", MODEL_COLOURS.PEOPLE_COLOR, MODEL_COLOURS.PEOPLE_HOVER_COLOR)
        s.nodeType = NODE_TYPE.PERSON

        s.description.push("<b>People</b> are individuals or teams that perform roles that do work.")
        s.description.push("<b>Roles</b> inherit capacity and costs from people.")

        if (people.length > 0) {
            s.description.push(`${this.getStartingMessage(individualCount, "individual")} and ${people.length-individualCount} teams, with a total of ${totalCount} people. They perform <b>${this.formatter.formatNumber(this.insights.superTypes[NODE_SUPERTYPE.PEOPLE].hours)}</b> hours which costs <b>${this.formatter.formatCost(getCostsForType(this.insights, NODE_SUPERTYPE.PEOPLE))}</b>.`)
            if (this.comparison) {
                s.description.push(`Current state worked <b>${this.formatter.formatNumber(this.comparisonInsights.superTypes[NODE_SUPERTYPE.PEOPLE].hours)}</b> hours at a cost of <b>${this.formatter.formatCost(getCostsForType(this.comparisonInsights, NODE_SUPERTYPE.PEOPLE))}</b>.`)
            }

            s.fields = this.setTopFive(this.linqset, s, people, this.filters, "People", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.PEOPLE)

        }

        return s
    }

    infrastructureSection():Section {
        const infrastructure = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFRASTRUCTURE).map(n => this.createTopFiveItemFromNode(n, this.linqset))
        if (infrastructure.length === 0) return undefined

        const s = new Section(INSIGHTS_SECTION.INFRASTRUCTURE, "Infrastructure", MODEL_COLOURS.INFRASTRUCTURE_COLOR, MODEL_COLOURS.INFRASTRUCTURE_HOVER_COLOR)
        s.nodeType = NODE_TYPE.INFRASTRUCTURE

        s.description.push("<b>Infrastructure</b> support systems. They are used to define costs and carbon emissions.")

        if (infrastructure.length > 0) {
            s.description.push(this.getMessage("infrastructure node", infrastructure.length, this.insights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].hours, getCostsForType(this.insights, NODE_SUPERTYPE.INFRASTRUCTURE), this.comparisonInsights?.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].hours, this.comparisonInsights &&  getCostsForType(this.comparisonInsights, NODE_SUPERTYPE.INFRASTRUCTURE)))

            s.fields = this.setTopFive(this.linqset, s, infrastructure, this.filters, "Infrastructure", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.INFRASTRUCTURE)
        }

        return s
    }

    systemsSection():Section {
        const systems = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).map(n => this.createTopFiveItemFromNode(n, this.linqset))
        if (systems.length == 0) return undefined

        const s = new Section(INSIGHTS_SECTION.SYSTEMS, "Systems", MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR)
        s.nodeType = NODE_TYPE.SYSTEM

        s.description.push("<b>Systems</b> are used to perform actions and provide information. <b>Infrastructure</b> may be set up to support systems with carbon emissions allocated to their usage.")
        if (systems.length > 0) {
            s.description.push(this.getMessage("system", systems.length, this.insights.superTypes[NODE_SUPERTYPE.ACTION].hours, getSystemInfrastructureModelCost(this.insights), this.comparisonInsights?.superTypes[NODE_SUPERTYPE.ACTION].hours, this.comparisonInsights && getSystemInfrastructureModelCost(this.comparisonInsights)))

            if (this.filters.indexOf(INSIGHTS_FIELD.CARBON) >= 0 || this.filters.indexOf(INSIGHTS_FIELD.CARBON_COST) >= 0 || this.filters.indexOf(INSIGHTS_FIELD.CARBON_ITEM) >= 0) {
                if (this.insights.allocatedCarbonByScope?.total > 0) {
                    Object.keys(this.insights.allocatedCarbonByScope.map).forEach(scope => {
                        if (this.insights.allocatedCarbonByScope.map[scope] && this.insights.allocatedCarbonByScope.map[scope] > 0)
                            s.description.push(`${this.getStartingMessage(this.insights.allocatedCarbonByScope.map[scope], "kgCO2e")} <b>${scope}</b> emissions. These are ${getCarbonScopeDescription(scope as CARBON_SCOPE)}.`)
                    })
                }
                if (this.insights.emissions && this.insights.emissions.length > 0) {
                    const sum = sumAllocations(this.insights.emissions)
                    s.description.push(`In total ${this.getStartingMessage(sum.allocated, "kgCO2e")} allocated, <b>${this.formatter.formatNumber(sum.unallocated)}</b> kgCO2e unallocated and <b>${this.formatter.formatNumber(sum.allocated)}</b> kgCO2e not modelled.`)
                }
                if (this.comparison) {
                    s.description.push(`There has been ${this.changeMessage(this.comparisonInsights.allocatedCarbonByScope.total, this.insights.allocatedCarbonByScope.total)} in carbon emissions compared to <b>${escapeHtml(this.comparison.workspaceName)}</b>`)
                }
            }
            s.fields = this.setTopFive(this.linqset, s, systems, this.filters, "Systems", NODE_TYPE_DATA_TYPE.STANDARD, NODE_SUPERTYPE.SYSTEM)

            const comparisonNodes = this.comparison?this.comparison.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).map(n => this.createTopFiveItemFromNode(n, this.comparison)):undefined
            this.addBubbleCharts(s, systems, comparisonNodes)

        }
        return s

    }

    addBubbleCharts(s:Section, nodes:TopFiveItem[], comparisonNodes:TopFiveItem[]|undefined) {
        if (!this.overview && nodes.length > 0) return

        let maxValue = 0

        const b1 = nodes.map(n => {
            if (maxValue < n.items) maxValue = n.items
            return new BubbleData(n._key, n.name, n.hours, n.cost, n.items)
        })
        const b2 = comparisonNodes && comparisonNodes.map(n => {
            if (maxValue < n.items) maxValue = n.items
            return new BubbleData(n._key, n.name, n.hours, n.cost, n.items)
        })

        const ds = {heading:s.title, labels:["Hours", "Cost", "Items"], data:[new BubbleDataSet(this.linqset.workspaceName, s.colour, s.hoverColour, b1)], maxValue}
        if (b2) ds.data.push(new BubbleDataSet("Current state", MODEL_COLOURS.EXCLUDED_COLOR, MODEL_COLOURS.EXCLUDED_HOVER_COLOR, b2))

        this.overview.bubbleData.push(ds)
    }

    createMostHoursCard(nodes:TopFiveItem[], superType:NODE_SUPERTYPE):InsightCard {
        const mostHours = nodes.reduce((f, n) => {
            if (n.hours > f.hours) f = n
            return f
        }, new TopFiveItem('', '', undefined, {} as NodeInsights))

        if (mostHours.hours) {
            const p = this.insights.superTypes[superType]?.hours?Math.round(100*mostHours.hours/this.insights.superTypes[superType]?.hours):0
            const v = this.formatter.formatNumber(mostHours.hours)
            const c = new InsightCard('Most hours: ' + mostHours.name, NODE_TYPE.SYSTEM, `${mostHours.name} does ${v} hours of work which is ${p}% of all work.`, undefined, mostHours.hours, undefined, INSIGHT_VALUE_TYPE.NUMBER, this.insights.superTypes[superType]?.hours)
            c.units = "hours"
            return c
        }
        return undefined
    }

    setTopFive(linqset:Linqset, section:Section, nodes:TopFiveItem[], filters:INSIGHTS_FIELD[], label:string, dataType:NODE_TYPE_DATA_TYPE, superType?:NODE_SUPERTYPE):SectionField[] {
        const result:SectionField[] = []

        if (nodes.length === 0) return []

        const fields = filters.map(o => superType ? contributionField(o, superType, dataType) : o)
        fields.forEach(o => {
            const f = new InsightsFieldType(o, linqset.modelKey, linqset.workspaceKey)

            if (!superType || f.doesFieldTypeApply(superType, dataType)) {
                const value = new Map(nodes.map(n => [n, f.getSingleValueFromInsights(n.insights, dataType)]))
                // nothing to rank when none of the section's nodes have any, such as hours for outcomes
                if (![...value.values()].some(v => v !== 0)) return

                const sorted = [...nodes].sort((a,b) => value.get(b)-value.get(a))

                // each node's share of its section: of all people's hours for a person, not the organisation's. Nodes
                // can share work (several systems on one action), so the organisation's total isn't the section's and
                // gave shares that were too small, or over 100%
                const total = [...value.values()].reduce((t, v) => t + v, 0)
                const share = (v:number) => total > 0 ? Math.round(100*v/total) : 0

                const top = sorted.slice(0,5).map(n => new InsightsRow(n._key, n.name, n.description, value.get(n), f.isCurrency(), share(value.get(n))))
                // dont do percentages for number processed if not using totals as doesnt make sense
                const p = (section.type === INSIGHTS_SECTION.WORKSPACES || section.type === INSIGHTS_SECTION.MODEL_TAGS || noPercentages.indexOf(o) >= 0)?false:this.includePercentages

                result.push(new SectionField(f.type, `${label} by ${f.getTitle(dataType, superType).toLowerCase()}`, f.getUnits(), f.getColours()[0], p, total, top))

                if (f.type === INSIGHTS_FIELD.WORKLOAD) {
                    const i = sorted.length > 5?sorted.length-5:0
                    const bottom = sorted.slice(i).map(n => new InsightsRow(n._key, n.name, n.description, value.get(n), f.isCurrency(), share(value.get(n)), f.getUnits(), )).reverse()
                    result.push(new SectionField(f.type, `${label} by lowest ${f.getTitle(dataType, superType).toLowerCase()}`, f.getUnits(), f.getColours()[0], p, total, bottom))
                }
            }
        })

        if (section.type === INSIGHTS_SECTION.MODEL_FUNCTIONS || section.type === INSIGHTS_SECTION.MODEL_TAGS || section.type === INSIGHTS_SECTION.WORKSPACES) {
            // add counts
            const top = nodes.sort((a,b) => b.count-a.count).slice(0,5).map(n => {
                const p = (section.type !== INSIGHTS_SECTION.WORKSPACES) && Math.round(100*n.count/this.linqset.models.length)
                return new InsightsRow(n._key, n.name, n.description, n.count, false, p)
            })

            const title = section.type === INSIGHTS_SECTION.WORKSPACES?"Number of changed models":"Model count"

            result.unshift(new SectionField(INSIGHTS_FIELD.NONE, title, 'models', MODEL_COLOURS.MODEL_COLOR, true, this.linqset.models.length, top))
        }

        return result
    }



    selectNode(key:string) {
        this.selectNodeEvent.emit(key)
    }

    onOptionsClick(change:MatChipListboxChange) {
        this.filterOptions = change.value
        this.setupData()
        this.disableSave = false
    }

    addComment(s:Section) {
        const data:CommentDialogData = {comment:s.comment, title:s.title, note:'Comments are kept with the filter when it is saved.'}
        const dialogRef = this.dialog.open(CommentDialogComponent, {data, width:'560px'});

        // cancelling, escape and clicking outside all close with nothing, which leaves the comment as it was
        return dialogRef.afterClosed().subscribe((comment:string|undefined) => {
            if (typeof comment === 'string') s.comment = comment
        })
    }

    savePreferences() {
        const user = this.userService.getUser()
        user.preferences.fieldTypes = this.filterOptions
        user.preferences.sections = this.sectionOptions
        this.userService.saveUserPreferences(user)
    }


    isGeneratingPdf() {
        return !!this.pdfMessage && this.pdfMessage !== "Completed" && this.pdfMessage !== "Failed"
    }

    generatePdf() {
        this.pdfMessage = "Starting PDF generation"
        setTimeout(() => this.generatePdf2())
    }

    // Captures the overview once and adds it to the PDF a section at a time, so page breaks fall between sections.
    // Capturing each section separately copied the page every time and took minutes.
    async generatePdf2() {
        const root = this.host.nativeElement.querySelector<HTMLElement>('.container')
        if (!root) {
            console.error('Overview not found for PDF');
            this.pdfMessage = "Failed"
            return;
        }

        // the comment buttons and view toggles are for the screen, not for the report
        const screenOnly = Array.from(root.querySelectorAll<HTMLElement>('[id^="button-"], .view-toggles'))
        try {
            this.pdfMessage = "Capturing the overview"
            this.changeDetectorRef.detectChanges()
            screenOnly.forEach(e => e.setAttribute("hidden", "true"))
            root.classList.add("pdf-capture")

            const parts = [
                root.querySelector<HTMLElement>('#model-heading'),
                root.querySelector<HTMLElement>('#overview-section'),
                root.querySelector<HTMLElement>('#charts'),
                ...this.sections.map(s => root.querySelector<HTMLElement>(`[id="section-${s.type}"]`)),
            ].filter((p): p is HTMLElement => !!p)

            const rootBox = root.getBoundingClientRect()
            const bands = parts.map(p => {
                const box = p.getBoundingClientRect()
                return { top: box.top - rootBox.top, bottom: box.bottom - rootBox.top }
            })
            // a long overview on a narrow screen is captured at a lower resolution, rather than failing to draw
            const scale = Math.min(PDF_SCALE, PDF_MAX_CANVAS_HEIGHT / Math.max(1, rootBox.height))
            const canvas = await this.pdfService.captureElement(root, scale)
            const pixelsPerCssPixel = canvas.width / rootBox.width

            const pdf = await this.pdfService.createPDF('p')
            let current:number|undefined
            for (const band of bands) {
                const slice = this.pdfService.sliceCanvas(canvas, band.top * pixelsPerCssPixel, band.bottom * pixelsPerCssPixel)
                current = this.pdfService.addCanvasToPdf(pdf, slice, current !== undefined ? current + PDF_SECTION_GAP : undefined)
            }

            // add image of model
            if (this.graph) {
                this.pdfMessage = "Adding the model graph"
                this.changeDetectorRef.detectChanges()
                await this.pdfService.generateGraph(this.graph, pdf)
            }
            this.pdfMessage = "Creating file"

            pdf.save(this.linqset.name + '-overview.pdf');

            this.pdfMessage = "Completed"
        } catch(error) {
            console.error('Error generating PDF', error);
            this.pdfMessage = "Failed"
        } finally {
            screenOnly.forEach(e => e.removeAttribute("hidden"))
            root.classList.remove("pdf-capture")
            // the capture finishes outside Angular's change detection, so show the result now
            this.changeDetectorRef.detectChanges()
        }
    }

    async generateStructuredPdf() {
        const pdf = new (await loadJsPDF())();
        const autoTable = (await import('jspdf-autotable')).default;  // We use this for tables

        // Title
        pdf.setFontSize(18);
        pdf.text('Overview', 14, 20);

        // Workspace card
        pdf.setFontSize(12);
        pdf.setTextColor(40);
        pdf.text('Workspace: Current state', 14, 30);
        pdf.text('Name: Marketing Admin', 14, 36);
        pdf.text('Description: Current state: Marketing Admin', 14, 42);

        // Node Numbers as colored boxes
        const nodeYStart = 50;
        const boxHeight = 10;
        const boxWidth = 40;

        const nodes = [
            { color: MODEL_COLOURS.ACTION_COLOR, label: 'Action', value: this.insights.superTypes[NODE_SUPERTYPE.ACTION].number },
            { color: MODEL_COLOURS.INFO_COLOR, label: 'Info', value: this.insights.superTypes[NODE_SUPERTYPE.INFORMATION].number },
            { color: MODEL_COLOURS.ROLE_COLOR, label: 'Role', value: this.insights.superTypes[NODE_SUPERTYPE.ROLE].number },
            { color: MODEL_COLOURS.SYSTEM_COLOR, label: 'System', value: this.insights.superTypes[NODE_SUPERTYPE.SYSTEM].number }
        ];

        let currentX = 14;
        let currentY = nodeYStart;

        nodes.forEach(node => {
            pdf.setFillColor(node.color);
            pdf.rect(currentX, currentY, boxWidth, boxHeight, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.text(`${node.label}: ${node.value}`, currentX + 2, currentY + 7);
            currentY += boxHeight + 4;
        });

        //const overviewBody = this.insightsData.map(d => [d.name, d.currency?this.formatter.formatCost(d.value):this.formatter.formatNumber(d.value), d.units])
        // Numbers table
        //autoTable(pdf, {
        //    startY: currentY + 4,
        //    head: [['Metric', 'Value', 'Unit']],
        //    body: overviewBody
        //});

        this.sections.forEach(s => {
            // Roles summary
            let afterTableY = (pdf as any).lastAutoTable.finalY + 10;
            pdf.setTextColor(s.colour);
            pdf.text(s.title, 14, afterTableY);
            pdf.setFontSize(10);
            pdf.setTextColor(0)
            s.description.forEach(d => {
                pdf.html(d);
            })

            s.fields.forEach(c => {
                autoTable(pdf, {
                    startY: afterTableY + 12,
                    head: [[c.title, ""]],
                    body: c.rows.map(r => [r.name, r.currency?this.formatter.formatCost(r.before):this.formatter.formatNumber(r.before)])
                })
            })
        })

        pdf.save('overview_structured.pdf');
    }

}
